/**
 * useSessionsLoader.js — Chargement de la liste globale des sessions et
 * fraîcheur au retour sur une liste (v10.5.307, D232).
 *
 * Pourquoi : la liste est un état global (useAppState) qu'aucun Retour ne
 * rechargeait. Un titre modifié ou un changement de cheval restait invisible
 * jusqu'au rafraîchissement du navigateur. Règle : une liste se recharge quand
 * on y revient, quel que soit le chemin, sauf si rien n'a pu la périmer depuis
 * le dernier chargement (pas de double requête au premier affichage).
 *
 * - Périmée : quand la liste quitte l'écran (leaveSessionsList) ou quand une
 *   page qui modifie les sessions s'affiche (markSessionsStale). Un chargement
 *   lancé pendant que la liste est affichée la rend fraîche ; un chargement
 *   lancé ailleurs (connexion, menu) non, l'arrivée sur la liste décide.
 * - enterSessionsList(horseId) recharge si périmée, sauf si une requête du même
 *   filtre est déjà en vol (menu puis montage de la liste : un seul appel).
 * - Seule la réponse de la dernière requête est appliquée (filtre changé vite).
 * - La liste affichée n'est jamais vidée pendant le rechargement : la vérité
 *   vient du serveur, sans mise à jour optimiste locale.
 */
import { useRef, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authFetch';

export default function useSessionsLoader({ setSessions, onUnauthorized }) {
  const staleRef   = useRef(false);
  const visibleRef = useRef(false);
  // Requête la plus récente : seq pour ignorer les réponses dépassées, key
  // (filtre cheval) tant qu'elle est en vol, null une fois terminée.
  const lastReqRef = useRef({ seq: 0, key: null });

  const loadSessions = useCallback(async (horseId = null) => {
    if (visibleRef.current) staleRef.current = false;
    const seq = lastReqRef.current.seq + 1;
    lastReqRef.current = { seq, key: horseId || '' };
    try {
      const url = horseId ? `/api/sessions?horse_id=${horseId}` : '/api/sessions';
      const res = await fetch(url, { headers: getAuthHeaders(), credentials: 'include' });
      if (res.status === 401) { onUnauthorized(); return; }
      const data = await res.json();
      if (data.success && seq === lastReqRef.current.seq) setSessions(data.sessions);
    } catch (e) { console.error('loadSessions:', e); }
    finally { if (seq === lastReqRef.current.seq) lastReqRef.current.key = null; }
  }, [setSessions, onUnauthorized]);

  const markSessionsStale = useCallback(() => { staleRef.current = true; }, []);

  const enterSessionsList = useCallback((horseId = null) => {
    visibleRef.current = true;
    if (!staleRef.current) return;
    if (lastReqRef.current.key === (horseId || '')) { staleRef.current = false; return; }
    loadSessions(horseId);
  }, [loadSessions]);

  const leaveSessionsList = useCallback(() => {
    visibleRef.current = false;
    staleRef.current = true;
  }, []);

  return { loadSessions, enterSessionsList, leaveSessionsList, markSessionsStale };
}
